import { FC, Fragment, MouseEventHandler } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { getSavingsOpportunityName } from "@/components/pages/savings-opportunities/utils/helpers/getSavingsOpportunityName";
import { SavingsOpportunityFilterSetIcon } from "@/components/pages/savings-opportunities/components/SavingsOpportunityFilterSetIcon";
import { money } from "@/utils/numeral/money";
import { getPercentage } from "@/utils/numeral/getPercentage";
import { opportunityTypeTitlesByActionType } from "@/components/pages/savings-opportunities/utils/constants/common";
import { SavingsOpportunityImplementationEffort } from "@/components/pages/savings-opportunities/components/SavingsOpportunityImplementationEffort";
import { SavingsOpportunity } from "@/services/cloudchipr.api";
import { ResourceDetailsOpportunityRowCheckboxColumn } from "@/components/pages/common/resource-details/components/content/opportunities-content/components/row-columns/ResourceDetailsOpportunityRowCheckboxColumn";

interface ResourceDetailsOpportunityRowColumnsProps {
  highest?: boolean;
  expanded: boolean;
  opportunity: SavingsOpportunity;
}

export const ResourceDetailsOpportunityRowColumns: FC<
  ResourceDetailsOpportunityRowColumnsProps
> = ({ opportunity, highest, expanded }) => {
  const filterSetId =
    opportunity.action_type === "live_filtered_resource" &&
    opportunity.filter_set_id;

  const pricePercent =
    !!opportunity.current_monthly_price &&
    Math.round(
      getPercentage(
        opportunity.monthly_savings ?? 0,
        opportunity.current_monthly_price,
      ),
    );

  return (
    <Fragment>
      <Stack
        flex={4}
        direction="row"
        spacing={0.5}
        overflow="hidden"
        alignItems="center"
      >
        <ResourceDetailsOpportunityRowCheckboxColumn
          opportunityId={opportunity.id}
        />

        <IconButton sx={{ p: 0.5 }} size="small">
          {!expanded ? (
            <KeyboardArrowRightIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>

        <TypographyWithTooltip
          title={getSavingsOpportunityName(
            opportunity?.action_type,
            opportunity?.service,
          )}
          fontWeight="medium"
          variant="body2"
        />

        {filterSetId && (
          <Box onClick={stopPropagation}>
            <SavingsOpportunityFilterSetIcon
              filterSetId={filterSetId}
              provider={opportunity.cloud_provider}
              accountId={opportunity?.account?.id ?? ""}
              resourceType={opportunity.service}
            />
          </Box>
        )}

        {highest && (
          <Typography
            variant="caption"
            px={0.5}
            bgcolor="success.light"
            borderRadius={2}
            color="white"
            align="center"
            lineHeight={1.5}
          >
            RECOMMENDED
          </Typography>
        )}
      </Stack>

      <Stack flex={2}>
        <Typography variant="body2">Monthly Savings</Typography>
        <Typography variant="body2" fontWeight="bold" color="success.light">
          {money(opportunity.monthly_savings)}{" "}
          {pricePercent && `(${pricePercent}%)`}
        </Typography>
      </Stack>

      <Stack flex={2}>
        <Typography variant="body2">Opportunity Type</Typography>
        <Typography variant="body2" fontWeight="bold">
          {opportunityTypeTitlesByActionType[opportunity.action_type]}
        </Typography>
      </Stack>

      <Stack flex={2} spacing={1}>
        <Typography variant="body2">Implementation Effort</Typography>
        <SavingsOpportunityImplementationEffort
          effort={opportunity.implementation_effort}
        />
      </Stack>
    </Fragment>
  );
};

const stopPropagation: MouseEventHandler = (e) => e.stopPropagation();
